
import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import SEOHead from '../../components/SEO/SEOHead';

export default function CatalogPage() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isDownloading, setIsDownloading] = useState(false);
  const catalogRef = useRef<HTMLDivElement>(null);

  const seoData = {
    title: 'کاتالوگ محصولات گلخانه سبز آریا - دانلود کاتالوگ PDF تجهیزات گلخانه',
    description: 'دانلود کاتالوگ کامل محصولات و تجهیزات گلخانه سبز آریا شامل مشخصات فنی، قیمت‌ها و راهنمای انتخاب. کاتالوگ PDF رایگان تمام محصولات کوکوپیت، نخ یووی، توری و تجهیزات.',
    keywords: 'کاتالوگ گلخانه آریا، دانلود کاتالوگ PDF، کاتالوگ محصولات گلخانه، مشخصات فنی تجهیزات، قیمت محصولات گلخانه، راهنمای انتخاب تجهیزات، کاتالوگ رایگان، بروشور محصولات',
    ogImage: 'https://public.readdy.ai/ai/img_res/f069d2036711cc1945f0feff1f502b69.jpg',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Catalog",
      "name": "کاتالوگ محصولات گلخانه سبز آریا",
      "description": "کاتالوگ کامل تجهیزات و محصولات گلخانه",
      "publisher": {
        "@type": "Organization",
        "name": "گلخانه سبز آریا"
      },
      "inLanguage": "fa",
      "dateModified": "2024-01-01",
      "about": {
        "@type": "Thing",
        "name": "تجهیزات گلخانه"
      }
    }
  };

  const categories = [
    { id: 'all', name: 'همه محصولات', icon: 'ri-apps-line', count: 15 },
    { id: 'substrates', name: 'بسترهای کشت', icon: 'ri-seedling-line', count: 4 },
    { id: 'support', name: 'تجهیزات نگهداری', icon: 'ri-plant-line', count: 3 },
    { id: 'protection', name: 'سیستم‌های محافظتی', icon: 'ri-shield-line', count: 3 },
    { id: 'heating', name: 'تجهیزات گرمایشی', icon: 'ri-fire-line', count: 2 },
    { id: 'tools', name: 'ابزار و لوازم جانبی', icon: 'ri-tools-line', count: 3 }
  ];

  const products = [
    {
      id: 1,
      name: 'کوکوپیت پریمیوم',
      category: 'substrates',
      code: 'CP-001',
      image: 'https://readdy.ai/api/search-image?query=premium%20coconut%20coir%20substrate%20blocks%20for%20greenhouse%20cultivation%20professional%20agricultural%20growing%20medium%20clean%20white%20background%20high%20quality%20product%20photography%20detailed%20texture&width=400&height=300&seq=catalog-coco&orientation=landscape',
      description: 'بستر کشت طبیعی و ارگانیک از الیاف نارگیل با کیفیت بالا',
      specifications: {
        'حجم': '5 کیلوگرم',
        'رطوبت': '15-20%',
        'pH': '5.5-6.5',
        'EC': '< 0.5 mS/cm',
        'منشأ': 'سریلانکا'
      },
      features: ['100% طبیعی و ارگانیک', 'نگهداری آب عالی', 'تهویه مناسب', 'pH متعادل', 'قابل استفاده مجدد'],
      applications: ['کشت گوجه فرنگی', 'کشت خیار', 'کشت فلفل', 'کشت سبزیجات برگی'],
      packaging: 'بسته‌بندی 5 کیلوگرمی فشرده',
      warranty: '6 ماه ضمانت کیفیت'
    },
    {
      id: 2,
      name: 'پیت ماس کانادایی',
      category: 'substrates',
      code: 'PM-002',
      image: 'https://readdy.ai/api/search-image?query=Canadian%20peat%20moss%20premium%20quality%20growing%20medium%20for%20greenhouse%20professional%20horticultural%20substrate%20clean%20packaging%20high%20resolution%20product%20photography&width=400&height=300&seq=catalog-peat&orientation=landscape',
      description: 'خاک مخصوص کشت گلخانه‌ای با کیفیت کانادایی',
      specifications: {
        'حجم': '3.8 کیوبیک فوت',
        'رطوبت': '45-55%',
        'pH': '3.5-4.5',
        'درجه تجزیه': 'H2-H4',
        'منشأ': 'کانادا'
      },
      features: ['کیفیت کانادایی', 'نگهداری رطوبت بالا', 'ساختار سبک', 'عاری از آفات', 'مناسب همه گیاهان'],
      applications: ['کشت گل و گیاه', 'نهالستان', 'کشت هیدروپونیک', 'ترکیب با سایر بسترها'],
      packaging: 'بسته‌بندی 3.8 کیوبیک فوت فشرده',
      warranty: '1 سال ضمانت کیفیت'
    },
    {
      id: 3,
      name: 'نخ UV گلخانه',
      category: 'support',
      code: 'UV-003',
      image: 'https://readdy.ai/api/search-image?query=UV%20resistant%20greenhouse%20twine%20rope%20for%20plant%20support%20professional%20agricultural%20equipment%20white%20background%20modern%20product%20photography%20strong%20durable%20material&width=400&height=300&seq=catalog-twine&orientation=landscape',
      description: 'نخ مقاوم در برابر اشعه UV برای نگهداری گیاهان',
      specifications: {
        'طول': '2000 متر',
        'ضخامت': '2 میلی‌متر',
        'مقاومت کششی': '15 کیلوگرم',
        'مواد': 'پلی‌پروپیلن',
        'محافظت UV': '4%'
      },
      features: ['مقاوم در برابر UV', 'استحکام بالا', 'انعطاف‌پذیر', 'ضد آب', 'قابل بازیافت'],
      applications: ['نگهداری گوجه فرنگی', 'نگهداری خیار', 'نگهداری فلفل', 'نگهداری لوبیا'],
      packaging: 'قرقره 2 کیلوگرمی',
      warranty: '1 سال ضمانت کیفیت'
    },
    {
      id: 4,
      name: 'توری ضد تریپس',
      category: 'protection',
      code: 'AT-004',
      image: 'https://readdy.ai/api/search-image?query=anti-thrips%20insect%20screen%20mesh%20for%20greenhouse%20protection%20fine%20mesh%20agricultural%20netting%20white%20background%20professional%20product%20photography%20detailed%20texture&width=400&height=300&seq=catalog-mesh&orientation=landscape',
      description: 'توری محافظ در برابر حشرات مضر گلخانه',
      specifications: {
        'ابعاد': '2×50 متر',
        'اندازه چشمه': '0.15×0.35 میلی‌متر',
        'رنگ': 'سفید',
        'مواد': 'پلی‌اتیلن',
        'ضخامت': '0.2 میلی‌متر'
      },
      features: ['محافظت از تریپس', 'تهویه مناسب', 'عبور نور', 'مقاوم در برابر UV', 'نصب آسان'],
      applications: ['محافظت از گوجه', 'محافظت از خیار', 'محافظت از فلفل', 'محافظت از گل‌ها'],
      packaging: 'رول 100 متری',
      warranty: '2 سال ضمانت کیفیت'
    },
    {
      id: 5,
      name: 'چسب ترمیم پلاستیک',
      category: 'tools',
      code: 'RT-005',
      image: 'https://readdy.ai/api/search-image?query=greenhouse%20plastic%20repair%20tape%20transparent%20adhesive%20tape%20for%20agricultural%20use%20professional%20quality%20white%20background%20product%20photography%20clear%20material&width=400&height=300&seq=catalog-tape&orientation=landscape',
      description: 'چسب شفاف برای ترمیم پلاستیک گلخانه',
      specifications: {
        'عرض': '5 سانتی‌متر',
        'طول': '25 متر',
        'ضخامت': '0.1 میلی‌متر',
        'دمای کاری': '-20 تا +80 درجه',
        'مواد': 'پلی‌اتیلن'
      },
      features: ['شفافیت بالا', 'چسبندگی قوی', 'مقاوم در برابر آب و هوا', 'آسان الاستفاده', 'بدون باقی‌مانده'],
      applications: ['ترمیم پلاستیک گلخانه', 'ترمیم تونل', 'ترمیم سایبان', 'کاربردهای عمومی'],
      packaging: 'رول 25 متری',
      warranty: '6 ماه ضمانت کیفیت'
    },
    {
      id: 6,
      name: 'هیتر تابشی مادون قرمز',
      category: 'heating',
      code: 'IH-006',
      image: 'https://readdy.ai/api/search-image?query=infrared%20radiant%20heater%20for%20greenhouse%20professional%20agricultural%20heating%20equipment%20modern%20design%20white%20background%20industrial%20product%20photography&width=400&height=300&seq=catalog-heater&orientation=landscape',
      description: 'سیستم گرمایشی تابشی برای گلخانه‌های بزرگ',
      specifications: {
        'قدرت': '3 کیلووات',
        'ولتاژ': '220 ولت',
        'ابعاد': '120×15×10 سانتی‌متر',
        'پوشش': '30 متر مربع',
        'وزن': '8 کیلوگرم'
      },
      features: ['گرمایش یکنواخت', 'صرفه‌جویی انرژی', 'کنترل دقیق دما', 'نصب آسان', 'عمر طولانی'],
      applications: ['گلخانه‌های تجاری', 'گلخانه‌های خانگی', 'نهالستان‌ها', 'سالن‌های کشاورزی'],
      packaging: 'جعبه کارتنی با لوازم نصب',
      warranty: '2 سال ضمانت کیفیت'
    }
  ];

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  const handleDownloadCatalog = async () => {
    setIsDownloading(true);
    
    // شبیه‌سازی فرآیند دانلود
    setTimeout(() => {
      // ایجاد لینک دانلود
      const link = document.createElement('a');
      link.href = 'data:application/pdf;base64,'; // در حالت واقعی باید PDF واقعی باشد
      link.download = 'catalog-greenhouse-aria-2024.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setIsDownloading(false);
      
      // نمایش پیام موفقیت
      alert('کاتالوگ با موفقیت دانلود شد!');
    }, 2000);
  };

  return (
    <div className="min-h-screen">
      <SEOHead {...seoData} />
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative h-96 flex items-center justify-center bg-cover bg-center pt-24 mt-20"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://readdy.ai/api/search-image?query=professional%20greenhouse%20equipment%20catalog%20cover%20page%20modern%20design%20agricultural%20products%20brochure%20clean%20layout%20with%20product%20images&width=1920&height=600&seq=catalog-hero&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-4 lg:px-8 text-center text-white">
          <h1 className="text-5xl lg:text-6xl font-bold mb-6">
            کاتالوگ محصولات
          </h1>
          <p className="text-xl lg:text-2xl max-w-3xl mx-auto mb-8">
            مجموعه کامل تجهیزات گلخانه با مشخصات فنی دقیق و راهنمای استفاده
          </p>
          <button 
            onClick={handleDownloadCatalog}
            disabled={isDownloading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-full text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isDownloading ? (
              <>
                <i className="ri-loader-4-line animate-spin ml-2"></i>
                در حال دانلود...
              </>
            ) : (
              <>
                <i className="ri-download-line ml-2"></i>
                دانلود کاتالوگ PDF
              </>
            )}
          </button>
        </div>
      </section>

      {/* Catalog Info */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-text-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">کاتالوگ کامل</h3>
              <p className="text-gray-600">شامل تمام محصولات با مشخصات فنی دقیق</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-image-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">تصاویر با کیفیت</h3>
              <p className="text-gray-600">عکس‌های حرفه‌ای از تمام محصولات</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-price-tag-3-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">اطلاعات قیمت</h3>
              <p className="text-gray-600">راهنمای قیمت و شرایط خرید</p>
            </div>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-emerald-50 border border-gray-200'
                }`}
              >
                <i className={`${category.icon} text-lg`}></i>
                <span>{category.name}</span>
                <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-1 rounded-full">
                  {category.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Catalog */}
      <section className="py-20" ref={catalogRef}>
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">
              {filteredProducts.length} محصول در این دسته
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              مشخصات فنی کامل، راهنمای استفاده و اطلاعات تکمیلی هر محصول
            </p>
          </div>
          
          <div className="space-y-12">
            {filteredProducts.map((product, index) => (
              <div key={product.id} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                {/* Product Image */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="relative">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-80 object-cover object-top rounded-3xl shadow-lg"
                    />
                    <div className="absolute top-4 right-4 bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      کد: {product.code}
                    </div>
                  </div>
                </div>

                {/* Product Details */}
                <div className={`${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <div className="bg-white p-8 rounded-3xl shadow-lg">
                    <h3 className="text-3xl font-bold text-gray-800 mb-4">{product.name}</h3>
                    <p className="text-gray-600 text-lg leading-relaxed mb-6">{product.description}</p>
                    
                    {/* Specifications */}
                    <div className="mb-6">
                      <h4 className="text-xl font-semibold text-gray-800 mb-4">مشخصات فنی:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(product.specifications).map(([key, value]) => (
                          <div key={key} className="flex justify-between py-2 border-b border-gray-200">
                            <span className="font-medium text-gray-700">{key}:</span>
                            <span className="text-gray-600">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mb-6">
                      <h4 className="text-xl font-semibold text-gray-800 mb-4">ویژگی‌ها:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {product.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <i className="ri-check-line text-emerald-600"></i>
                            <span className="text-gray-700 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Applications */}
                    <div className="mb-6">
                      <h4 className="text-xl font-semibold text-gray-800 mb-4">کاربردها:</h4>
                      <div className="flex flex-wrap gap-2">
                        {product.applications.map((app, idx) => (
                          <span key={idx} className="bg-emerald-100 text-emerald-700 text-sm px-3 py-1 rounded-full">
                            {app}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Additional Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-gray-800 mb-2">بسته‌بندی:</h5>
                        <p className="text-gray-600 text-sm">{product.packaging}</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-gray-800 mb-2">ضمانت:</h5>
                        <p className="text-gray-600 text-sm">{product.warranty}</p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link 
                        to={`/products/${product.id}`}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-7

                        text-white text-center px-6 py-3 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-eye-line ml-2"></i>
                        مشاهده جزئیات
                      </Link>
                      <button className="flex-1 border border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-6 py-3 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap">
                        <i className="ri-phone-line ml-2"></i>
                        استعلام قیمت
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Support */}
      <section className="py-20 bg-emerald-600">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">پشتیبانی فنی تخصصی</h2>
              <p className="text-lg leading-relaxed mb-6">
                تیم متخصص ما آماده ارائه مشاوره رایگان برای انتخاب بهترین محصولات متناسب با نیاز گلخانه شما است.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-2xl text-emerald-200"></i>
                  <span className="text-lg">مشاوره رایگان انتخاب محصول</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-2xl text-emerald-200"></i>
                  <span className="text-lg">راهنمایی نصب و استفاده</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-2xl text-emerald-200"></i>
                  <span className="text-lg">پشتیبانی پس از فروش</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-check-line text-2xl text-emerald-200"></i>
                  <span className="text-lg">آموزش کاربردی</span>
                </div>
              </div>
              <div className="mt-8">
                <button className="bg-white text-emerald-600 px-8 py-3 rounded-full font-bold hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line ml-2"></i>
                  تماس با کارشناسان
                </button>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=professional%20agricultural%20consultant%20helping%20farmer%20in%20modern%20greenhouse%20technical%20support%20expert%20explaining%20equipment%20usage%20Iranian%20specialist%20with%20customer%20clean%20bright%20environment&width=600&height=500&seq=catalog-support&orientation=landscape"
                alt="پشتیبانی فنی"
                className="rounded-3xl shadow-2xl object-cover object-top w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Download CTA */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-6">
            دانلود کاتالوگ کامل
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            کاتالوگ PDF شامل تمام محصولات، مشخصات فنی، راهنمای استفاده و اطلاعات قیمت
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <i className="ri-file-pdf-line text-4xl text-red-600 mb-4"></i>
              <h3 className="text-lg font-bold text-gray-800 mb-2">فرمت PDF</h3>
              <p className="text-gray-600 text-sm">قابل مشاهده در تمام دستگاه‌ها</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <i className="ri-download-cloud-line text-4xl text-blue-600 mb-4"></i>
              <h3 className="text-lg font-bold text-gray-800 mb-2">دانلود رایگان</h3>
              <p className="text-gray-600 text-sm">بدون نیاز به ثبت‌نام</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <i className="ri-refresh-line text-4xl text-green-600 mb-4"></i>
              <h3 className="text-lg font-bold text-gray-800 mb-2">به‌روزرسانی مداوم</h3>
              <p className="text-gray-600 text-sm">آخرین محصولات و قیمت‌ها</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={handleDownloadCatalog}
              disabled={isDownloading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-full text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isDownloading ? (
                <>
                  <i className="ri-loader-4-line animate-spin ml-2"></i>
                  در حال دانلود...
                </>
              ) : (
                <>
                  <i className="ri-download-line ml-2"></i>
                  دانلود کاتالوگ PDF
                </>
              )}
            </button>
            <button className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-4 rounded-full text-lg font-bold transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-mail-line ml-2"></i>
              ارسال به ایمیل
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
